import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { APP_NAME, APP_TAGLINE } from '@/constants';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { useBrandingStore } from '@/store/brandingStore';

export type AppLogoVariant = 'mark' | 'full';

interface AppLogoProps {
  variant?: AppLogoVariant;
  showTagline?: boolean;
  /** Force the light-on-dark wordmark (navy sidebar, auth panel). */
  onDark?: boolean;
  sx?: SxProps<Theme>;
}

const FULL_LIGHT_SRC = '/logo-default-white.png';
const FULL_DARK_SRC = '/logo-default.png';
const MARK_SRC = '/appicon.png';

export function AppLogo({ variant = 'full', showTagline = false, onDark, sx }: AppLogoProps) {
  const { mode } = useThemeMode();
  const { logoUrl, faviconUrl, siteName, tagline } = useBrandingStore();

  // logo-default-white.png has dark text (for white/light backgrounds)
  // logo-default.png has white text (for dark backgrounds)
  const isDarkTarget = Boolean(onDark || mode === 'dark');
  const defaultFullSrc = isDarkTarget ? FULL_DARK_SRC : FULL_LIGHT_SRC;
  const brandName = siteName?.trim() || APP_NAME;
  const brandTagline = tagline?.trim() || APP_TAGLINE;

  if (variant === 'mark') {
    const markSrc = faviconUrl?.trim() || logoUrl?.trim() || MARK_SRC;
    return (
      <Box
        component="img"
        src={markSrc}
        alt={brandName}
        onError={(e) => {
          if (e.currentTarget.src !== MARK_SRC) {
            e.currentTarget.src = MARK_SRC;
          }
        }}
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block',
          objectFit: 'contain',
          ...sx,
        }}
      />
    );
  }

  const fullSrc =
    !logoUrl || logoUrl === '/logo-default.png' || logoUrl === '/logo-default-white.png'
      ? defaultFullSrc
      : logoUrl.trim();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, ...sx }}>
      <Box
        component="img"
        src={fullSrc}
        alt={brandName}
        onError={(e) => {
          if (e.currentTarget.src !== defaultFullSrc) {
            e.currentTarget.src = defaultFullSrc;
          }
        }}
        sx={{
          width: '100%',
          height: 'auto',
          maxWidth: '100%',
          display: 'block',
          objectFit: 'contain',
        }}
      />
      {showTagline ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {brandTagline}
        </Typography>
      ) : null}
    </Box>
  );
}
